/**
 * Enhanced input handling for Ragsmith Web UI
 * - Paste detection with attachment conversion for large text
 * - Image paste and drag-and-drop support
 * - Removable attachment chips with image thumbnails
 * - Auto-resize textarea
 */

import { mediaUrl } from './api.js';

// Configuration
const PASTE_THRESHOLD = 500;  // Characters before converting to attachment
const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_SIZE = 100000;  // ~100KB per text attachment
const MAX_IMAGE_SIZE = 2 * 1024 * 1024;  // 2MB after compression
const MAX_IMAGE_DIMENSION = 2048;  // Max width/height for compression
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
// Video rides differently from every other attachment: it is uploaded to
// /chat/media/upload first and the attachment carries only the returned
// media_id. A 250MB clip has no business in a JSON body, in the messages
// table, or being re-read out of SQLite on every subsequent turn.
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'];
const MAX_VIDEO_BYTES = 250 * 1024 * 1024;
const MAX_VIDEO_SECONDS = 600;
const MAX_VIDEOS = 1;  // each costs a transcription plus up to 8 vision calls

// Attachment state
const attachments = [];
let attachmentCounter = 0;

/**
 * Generate unique attachment ID
 */
function generateId() {
    return `att_${Date.now()}_${++attachmentCounter}`;
}

/**
 * Format bytes to human-readable size
 */
function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get icon for attachment type
 */
function getIcon(type) {
    switch (type) {
        case 'code': return '📝';
        case 'text': return '📄';
        case 'image': return '🖼️';
        case 'video': return '🎬';
        default: return '📎';
    }
}

/**
 * Detect if content looks like code
 */
function detectContentType(content) {
    // Simple heuristics for code detection
    const codeIndicators = [
        /^\s*(import|export|const|let|var|function|class|def|async|await)\s/m,
        /^\s*(if|for|while|switch|try|catch)\s*\(/m,
        /[{}[\]();]/,
        /^\s*\/\//m,
        /^\s*#\s*(include|define|pragma|import)/m,
        /=>/,
        /\w+\s*\([^)]*\)\s*{/,
    ];

    const codeMatches = codeIndicators.filter(re => re.test(content)).length;
    return codeMatches >= 2 ? 'code' : 'text';
}

/**
 * Generate a name for the attachment
 */
function generateName(content, type) {
    const count = attachments.length + 1;
    if (type === 'code') {
        return `code_${count}`;
    }
    if (type === 'image') {
        return `image_${count}`;
    }
    return `pasted_${count}`;
}

/**
 * Add a text attachment
 */
export function addTextAttachment(content) {
    if (attachments.length >= MAX_ATTACHMENTS) {
        return null;
    }

    if (content.length > MAX_ATTACHMENT_SIZE) {
        content = content.substring(0, MAX_ATTACHMENT_SIZE);
    }

    const type = detectContentType(content);
    const attachment = {
        id: generateId(),
        type,
        content,
        name: generateName(content, type),
        size: content.length
    };

    attachments.push(attachment);
    renderAttachments();
    return attachment;
}

/**
 * Process an image file (from paste or drag-and-drop)
 */
export function processImageFile(file) {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        return;
    }

    if (file.size > 20 * 1024 * 1024) {
        return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
        const dataUrl = e.target.result;
        const mediaType = file.type;

        // Check if compression needed
        if (file.size > MAX_IMAGE_SIZE) {
            compressImage(dataUrl, mediaType, file.name);
        } else {
            // Extract raw base64 (strip data URI prefix)
            const base64 = dataUrl.split(',')[1];
            addImageAttachment(base64, mediaType, file.name);
        }
    };
    reader.readAsDataURL(file);
}

/**
 * Compress an image using Canvas API
 */
function compressImage(dataUrl, originalMediaType, fileName) {
    const img = new Image();
    img.onload = () => {
        let { width, height } = img;

        // Scale down if exceeds max dimension
        if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            const scale = MAX_IMAGE_DIMENSION / Math.max(width, height);
            width = Math.round(width * scale);
            height = Math.round(height * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Encode as JPEG for compression
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
        const base64 = compressedDataUrl.split(',')[1];
        addImageAttachment(base64, 'image/jpeg', fileName);
    };
    img.src = dataUrl;
}

/**
 * Add an image attachment
 */
export function addImageAttachment(base64Data, mediaType, name, meta = null) {
    if (attachments.length >= MAX_ATTACHMENTS) {
        return null;
    }

    const sizeBytes = Math.ceil(base64Data.length * 3 / 4);  // Approximate decoded size

    const attachment = {
        id: generateId(),
        type: 'image',
        media_type: mediaType,
        // Kept for the composer preview only. Once the upload lands, the
        // request carries media_id instead — see the payload map in chat.js.
        content: base64Data,  // Raw base64 (no data URI prefix)
        name: name || generateName(null, 'image'),
        // How the picture was taken, when known; see camera.js lastCapture.
        meta: meta || null,
        size: sizeBytes,
        uploading: true,
        progress: 0,
    };

    attachments.push(attachment);
    renderAttachments();

    // Upload in the background, exactly as video does. Deliberately not
    // awaited: the three call sites are synchronous, and the send path
    // already refuses to fire while anything is still uploading.
    //
    // An image that fails to upload keeps its base64 and sends the old way.
    // That is the right fallback: a photo the user can see in the composer
    // must not vanish because a worker is asleep, and an inline image still
    // works — it just lives in the message row.
    uploadImageBytes(base64Data, mediaType, attachment)
        .catch(() => { attachment.uploading = false; renderAttachments(); });

    return attachment;
}

async function uploadImageBytes(base64Data, mediaType, attachment) {
    const bin = atob(base64Data);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
    const ext = (mediaType || 'image/png').split('/')[1].replace('jpeg', 'jpg');
    const file = new File([buf], attachment.name || `image.${ext}`, { type: mediaType });

    const form = new FormData();
    form.append('file', file, file.name);
    // How the picture was taken, when the composer knows (the viewfinder
    // does). The server keeps what it recognises in the file's sidecar.
    if (attachment.meta) form.append('meta', JSON.stringify(attachment.meta));
    const { getHeaders } = await import('./api.js');
    const headers = { ...(getHeaders(false) || {}) };
    delete headers['Content-Type'];   // the browser writes the multipart boundary

    const res = await fetch('/chat/media/upload', { method: 'POST', headers, body: form });
    if (!res.ok) throw new Error(`upload failed (${res.status})`);
    const meta = await res.json();

    attachment.media_id = meta.media_id;
    attachment.media_type = meta.media_type || attachment.media_type;
    attachment.size = meta.size ?? attachment.size;
    attachment.uploading = false;
    renderAttachments();
    return attachment;
}

/**
 * Read a video's duration client-side, so a too-long clip is rejected before
 * we spend a 250MB upload finding out.
 */
function readVideoDuration(file) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const probe = document.createElement('video');
        probe.preload = 'metadata';
        let settled = false;
        const done = (value) => {
            if (settled) return;
            settled = true;
            URL.revokeObjectURL(url);
            // Infinity/NaN is not a duration. A WebM written by MediaRecorder
            // has no duration in its header (it was a live stream), and the
            // browser reports Infinity — which rendered as "Infinity:NaN" and
            // failed the length check on every recorded clip.
            resolve(Number.isFinite(value) && value > 0 ? value : null);
        };
        probe.onloadedmetadata = () => {
            if (Number.isFinite(probe.duration) && probe.duration > 0) return done(probe.duration);
            // Force the browser to scan for the real end by seeking past it.
            probe.ontimeupdate = () => { probe.ontimeupdate = null; done(probe.duration); };
            probe.currentTime = 1e101;
        };
        probe.onerror = () => done(null);  // unreadable here; the server re-checks
        setTimeout(() => done(null), 3000);  // never hang the composer on a probe
        probe.src = url;
    });
}

function fmtDuration(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0) return '';
    const s = Math.max(0, Math.round(seconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Upload a video and add it as an attachment carrying its media_id.
 *
 * The chip appears immediately in a pending state with a progress bar —
 * uploading a phone video over cellular is slow enough that a composer which
 * looks like nothing happened reads as broken.
 */
export async function processVideoFile(file, knownDuration = null) {
    if (!ALLOWED_VIDEO_TYPES.includes(file.type) && !/\.(mp4|mov|webm|m4v)$/i.test(file.name || '')) {
        showAttachError('That video format is not supported. Use MP4, MOV or WebM.');
        return null;
    }
    if (attachments.filter(a => a.type === 'video').length >= MAX_VIDEOS) {
        showAttachError('One video per message.');
        return null;
    }
    if (attachments.length >= MAX_ATTACHMENTS) return null;
    if (file.size > MAX_VIDEO_BYTES) {
        showAttachError(`That video is ${formatSize(file.size)} — the limit is ${MAX_VIDEO_BYTES / (1024 * 1024)}MB.`);
        return null;
    }

    // A recording knows its own length; only probe when we weren't told.
    const duration = Number.isFinite(knownDuration) && knownDuration > 0
        ? knownDuration
        : await readVideoDuration(file);
    // duration === null means "could not tell" — never reject on that. The
    // server ffprobes the real length and enforces the limit properly.
    if (duration !== null && duration > MAX_VIDEO_SECONDS) {
        showAttachError(`That video is ${fmtDuration(duration)} — the limit is ${MAX_VIDEO_SECONDS / 60} minutes. Trim it, or use Recordings.`);
        return null;
    }

    const pending = {
        id: generateId(),
        type: 'video',
        media_type: file.type || 'video/mp4',
        content: '',            // filled with the media_id on success
        name: file.name || 'video.mp4',
        size: file.size,
        duration: duration || 0,   // 0 = unknown; the server's ffprobe fills it in
        uploading: true,
        progress: 0,
    };
    attachments.push(pending);
    renderAttachments();

    try {
        const meta = await uploadVideo(file, (pct) => {
            pending.progress = pct;
            // Update the live chip IN PLACE: rebuilding the whole container
            // per percent tick recreated the <img> poster and progress bar,
            // which read as flicker on every number change. Full re-render
            // only if the chip isn't in the DOM yet.
            const chip = document.querySelector(`.attachment-chip[data-id="${pending.id}"]`);
            const bar = chip?.querySelector('.attach-progress-bar');
            const label = chip?.querySelector('.attachment-size');
            if (bar && label) {
                bar.style.width = `${pct}%`;
                label.textContent = `uploading ${pct}%`;
            } else {
                renderAttachments();
            }
        });
        pending.content = meta.media_id;
        pending.media_type = meta.media_type || pending.media_type;
        pending.size = meta.size ?? pending.size;
        pending.duration = meta.duration ?? pending.duration;
        pending.has_audio = meta.has_audio;
        pending.uploading = false;
        renderAttachments();
        return pending;
    } catch (e) {
        const idx = attachments.findIndex(a => a.id === pending.id);
        if (idx >= 0) attachments.splice(idx, 1);
        renderAttachments();
        showAttachError(e.message || 'Video upload failed.');
        return null;
    }
}

/**
 * XMLHttpRequest rather than fetch: fetch still cannot report upload progress,
 * and a progress bar is the whole point for a file this size.
 */
function uploadVideo(file, onProgress) {
    return new Promise((resolve, reject) => {
        const form = new FormData();
        form.append('file', file, file.name || 'video.mp4');

        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/chat/media/upload');
        import('./api.js').then(({ getHeaders }) => {
            const headers = getHeaders(false) || {};
            // Content-Type must stay unset so the browser writes the multipart boundary.
            Object.entries(headers).forEach(([k, v]) => {
                if (k.toLowerCase() !== 'content-type') xhr.setRequestHeader(k, v);
            });
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
            };
            xhr.onload = () => {
                let body = {};
                try { body = JSON.parse(xhr.responseText || '{}'); } catch { /* non-JSON error page */ }
                if (xhr.status >= 200 && xhr.status < 300) resolve(body);
                else reject(new Error(body.detail || `Upload failed (${xhr.status})`));
            };
            xhr.onerror = () => reject(new Error('Upload failed — check your connection.'));
            xhr.send(form);
        }).catch(reject);
    });
}

/**
 * True while any attachment is still uploading — send must wait, or the
 * message goes out referencing a media_id the server does not have yet.
 */
export function hasPendingUploads() {
    return attachments.some(a => a.uploading);
}

/**
 * Remove attachment by ID
 */
export function removeAttachment(id) {
    const index = attachments.findIndex(a => a.id === id);
    if (index !== -1) {
        const [gone] = attachments.splice(index, 1);
        // A video that was uploaded and then unattached has bytes on the
        // server that nothing will ever reference — drop them now rather than
        // leaving orphans for a pruner to find later.
        if (gone?.type === 'video' && gone.content) {
            import('./api.js').then(({ getHeaders }) =>
                fetch(`/chat/media/${gone.content}`, { method: 'DELETE', headers: getHeaders(false) })
            ).catch(() => { /* best effort; an orphan is not worth an error toast */ });
        }
        renderAttachments();
    }
}

/**
 * Get all current attachments
 */
export function getAttachments() {
    return [...attachments];
}

/**
 * Clear all attachments
 */
export function clearAttachments() {
    attachments.length = 0;
    renderAttachments();
}

/**
 * Render attachment chips to the container
 */
function renderAttachments() {
    const container = document.getElementById('attachments-container');
    if (!container) return;

    if (attachments.length === 0) {
        container.classList.remove('has-attachments');
        container.innerHTML = '';
        return;
    }

    container.classList.add('has-attachments');
    container.innerHTML = attachments.map(att => {
        if (att.type === 'video') {
            // Must carry auth in the query string — an <img> cannot send
            // headers, and a bare path 401s (the broken-thumbnail bug).
            const poster = att.content
                ? `<img class="attachment-thumbnail" src="${mediaUrl(att.content, 'poster')}" alt="" />`
                : '<span class="attachment-icon">🎬</span>';
            const dur = fmtDuration(att.duration);
            const state = att.uploading
                ? `<span class="attachment-size">uploading ${att.progress || 0}%</span>
                   <span class="attach-progress"><span class="attach-progress-bar" style="width:${att.progress || 0}%"></span></span>`
                : `<span class="attachment-size">${dur ? `${dur} · ` : ''}${formatSize(att.size)}</span>`;
            return `
                <div class="attachment-chip video-chip${att.uploading ? ' uploading' : ''}" data-id="${att.id}">
                    ${poster}
                    <span class="attachment-name">${att.name}</span>
                    ${state}
                    <button class="attachment-remove" aria-label="Remove attachment">&times;</button>
                </div>
            `;
        }
        if (att.type === 'image') {
            return `
                <div class="attachment-chip image-chip" data-id="${att.id}">
                    <img class="attachment-thumbnail"
                         src="data:${att.media_type};base64,${att.content}"
                         alt="${att.name}" />
                    <span class="attachment-name">${att.name}</span>
                    <span class="attachment-size">(${formatSize(att.size)})</span>
                    <button class="attachment-remove" aria-label="Remove attachment">&times;</button>
                </div>
            `;
        }
        return `
            <div class="attachment-chip" data-id="${att.id}">
                <span class="attachment-icon">${getIcon(att.type)}</span>
                <span class="attachment-name">${att.name}</span>
                <span class="attachment-size">(${formatSize(att.size)})</span>
                <button class="attachment-remove" aria-label="Remove attachment">&times;</button>
            </div>
        `;
    }).join('');

    // Add click handlers for remove buttons
    container.querySelectorAll('.attachment-remove').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const chip = btn.closest('.attachment-chip');
            if (chip) {
                removeAttachment(chip.dataset.id);
            }
        });
    });
}

/**
 * Extract an image from HTML content (handles mobile browsers that put images as text/html)
 */
function extractImageFromHtml(html) {
    if (!html) return null;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const img = doc.querySelector('img[src]');
    if (!img) return null;

    const src = img.getAttribute('src');
    if (src.startsWith('data:image/')) {
        // data URI — extract base64 and media type
        const match = src.match(/^data:(image\/[^;]+);base64,(.+)$/);
        if (match) {
            return { mediaType: match[1], base64: match[2] };
        }
    } else if (src.startsWith('http')) {
        // External URL — return for fetching
        return { url: src };
    }
    return null;
}

/**
 * Handle paste event on the input
 */
function handlePaste(e) {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // Strategy 1: Check clipboardData.items for image blobs (desktop browsers)
    const items = clipboardData.items;
    if (items) {
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                e.preventDefault();
                const file = items[i].getAsFile();
                if (file) {
                    processImageFile(file);
                }
                return;
            }
        }
    }

    // Strategy 2: Check clipboardData.files (some mobile browsers populate this instead)
    const files = clipboardData.files;
    if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
            if (files[i].type.startsWith('image/')) {
                e.preventDefault();
                processImageFile(files[i]);
                return;
            }
        }
    }

    // Strategy 3: Check text/html for embedded images (mobile "Copy Image" often uses this)
    const html = clipboardData.getData('text/html');
    if (html) {
        const imageData = extractImageFromHtml(html);
        if (imageData) {
            e.preventDefault();
            if (imageData.base64) {
                addImageAttachment(imageData.base64, imageData.mediaType, null);
            } else if (imageData.url) {
                // Fetch the external image URL and convert to blob
                fetch(imageData.url)
                    .then(r => r.blob())
                    .then(blob => {
                        if (blob.type.startsWith('image/')) {
                            processImageFile(new File([blob], 'pasted-image', { type: blob.type }));
                        }
                    })
                    .catch(() => {});
            }
            return;
        }
    }

    // Check for text
    const text = clipboardData.getData('text/plain');
    if (text && text.length > PASTE_THRESHOLD) {
        e.preventDefault();
        addTextAttachment(text);
        return;
    }

    // Small text: let it paste normally
}

/**
 * Format message with attachments for sending
 */
export function formatMessageWithAttachments(message, attachmentList) {
    if (!attachmentList || attachmentList.length === 0) {
        return message;
    }

    // Only format text/code attachments as inline blocks; images are sent separately
    const textAttachments = attachmentList.filter(att => att.type !== 'image');
    const imageAttachments = attachmentList.filter(att => att.type === 'image');

    const parts = [];
    if (textAttachments.length > 0) {
        const attachmentBlocks = textAttachments.map(att => {
            return `[Attachment: ${att.name} (${formatSize(att.size)})]\n\`\`\`\n${att.content}\n\`\`\``;
        });
        parts.push(attachmentBlocks.join('\n\n'));
    }

    if (imageAttachments.length > 0) {
        const imageRefs = imageAttachments.map(att => `[Image: ${att.name}]`);
        parts.push(imageRefs.join(' '));
    }

    const separator = message.trim() ? '\n\n---\n\n' : '';
    return parts.join('\n\n') + separator + message;
}

/**
 * Show a brief error message near the input area
 */
function showAttachError(msg) {
    const inputArea = document.getElementById('input-area');
    if (!inputArea) return;

    // Remove any existing error
    inputArea.querySelector('.attach-error')?.remove();

    const el = document.createElement('div');
    el.className = 'attach-error';
    el.textContent = msg;
    inputArea.prepend(el);

    setTimeout(() => el.remove(), 3000);
}

/**
 * Initialize input handling
 */
export function initInput() {
    const messageInput = document.getElementById('message-input');
    if (!messageInput) {
        console.error('[Input] message-input not found');
        return;
    }

    // Paste handler
    messageInput.addEventListener('paste', handlePaste);

    // File picker change handler (trigger comes from plus menu)
    const fileInput = document.getElementById('image-file-input');
    if (fileInput) {
        fileInput.addEventListener('change', () => {
            let hasUnsupported = false;
            for (const file of fileInput.files) {
                if (file.type.startsWith('image/')) {
                    processImageFile(file);
                } else if (file.type.startsWith('video/') || /\.(mp4|mov|webm|m4v)$/i.test(file.name || '')) {
                    processVideoFile(file);
                } else {
                    hasUnsupported = true;
                }
            }
            if (hasUnsupported) {
                showAttachError('Images (PNG, JPEG, GIF, WebP) and video (MP4, MOV, WebM) only');
            }
            fileInput.value = '';  // Reset so same file can be re-selected
        });
    }

    // Auto-resize on input (enhanced)
    messageInput.addEventListener('input', () => {
        messageInput.style.height = 'auto';
        messageInput.style.height = Math.min(messageInput.scrollHeight, 150) + 'px';
    });

    // Drag-and-drop support on the input area
    const inputArea = document.getElementById('input-area');
    if (inputArea) {
        inputArea.addEventListener('dragover', (e) => {
            e.preventDefault();
            inputArea.classList.add('drag-over');
        });

        inputArea.addEventListener('dragenter', (e) => {
            e.preventDefault();
            inputArea.classList.add('drag-over');
        });

        inputArea.addEventListener('dragleave', (e) => {
            // Only remove if leaving the input area entirely
            if (!inputArea.contains(e.relatedTarget)) {
                inputArea.classList.remove('drag-over');
            }
        });

        inputArea.addEventListener('drop', (e) => {
            e.preventDefault();
            inputArea.classList.remove('drag-over');

            const files = e.dataTransfer.files;
            for (let i = 0; i < files.length; i++) {
                if (files[i].type.startsWith('image/')) {
                    processImageFile(files[i]);
                } else if (files[i].type.startsWith('video/')) {
                    processVideoFile(files[i]);
                }
            }
        });
    }
}
